import { FC } from 'react'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { useMobileScreens } from '@mobileHooks'
import { Autocomplete, Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material'

interface ScreensSelectorProps {
  selectedScreenIds: number[]
  startScreen: string
  onChange: (selectedIds: number[]) => void
  onStartScreenChange: (startScreen: string) => void
}

export const ScreensSelector: FC<ScreensSelectorProps> = ({
  selectedScreenIds,
  startScreen,
  onChange,
  onStartScreenChange,
}) => {
  const { screens, loading } = useMobileScreens()

  const selectedScreens = screens.filter(screen => selectedScreenIds.includes(screen.id))

  const handleToggleScreen = (screenId: number) => {
    const isSelected = selectedScreenIds.includes(screenId)
    if (isSelected) {
      onChange(selectedScreenIds.filter(id => id !== screenId))

      const removedScreen = screens.find(s => s.id === screenId)
      if (removedScreen && removedScreen.internalId === startScreen) {
        onStartScreenChange('')
      }
    } else {
      onChange([...selectedScreenIds, screenId])
    }
  }

  const handleToggleAll = () => {
    if (selectedScreenIds.length === screens.length) {
      onChange([])
      onStartScreenChange('')
    } else {
      onChange(screens.map(s => s.id))
    }
  }

  const columns = [
    {
      field: 'selected',
      headerName: 'Selected',
      width: 100,
      renderCell: (params: any) => (
        <Checkbox
          checked={selectedScreenIds.includes(params.row.id)}
          onChange={() => handleToggleScreen(params.row.id)}
        />
      ),
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 100,
    },
  ]

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Start Screen
        </Typography>
        <Autocomplete
          value={selectedScreens.find(s => s.internalId === startScreen) || null}
          onChange={(_, newValue) => {
            onStartScreenChange(newValue?.internalId || '')
          }}
          options={selectedScreens}
          getOptionLabel={option => `${option.title} [${option.internalId}] (${option.type})`}
          disabled={selectedScreens.length === 0}
          renderInput={params => (
            <TextField
              {...params}
              label='Start Screen'
              helperText={
                selectedScreens.length === 0
                  ? 'Please select at least one screen first'
                  : 'Select the initial screen to show when app opens'
              }
              required
            />
          )}
          fullWidth
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h6'>Select Screens for this App</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedScreenIds.length === screens.length && screens.length > 0}
              indeterminate={
                selectedScreenIds.length > 0 && selectedScreenIds.length < screens.length
              }
              onChange={handleToggleAll}
            />
          }
          label='Select All'
        />
      </Box>

      <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
        Selected: {selectedScreenIds.length} / {screens.length}
      </Typography>

      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={screens}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          hideFooter
        />
      </Box>
    </Box>
  )
}
